/**
 * Workspace directory for the picker. app.list_my_workspaces returns the caller's
 * own memberships only (bounded to 1,000), so local sorting/filtering here is a
 * full-result operation over a complete list, not a sort of a partial page.
 */
import type { Transport } from './transport.ts';

export interface WorkspaceRaw {
  workspace_id: string;
  name: string;
  workspace_status: 'active' | 'closing' | 'suspended' | 'deleting';
  membership_status: 'active' | 'suspended';
  role_name: string;
  last_opened_at: string | null;
  favorite: boolean;
}

export interface Workspace {
  id: string;
  name: string;
  role: string;
  available: boolean;
  /** Why the workspace cannot be opened, when it cannot. */
  unavailableReason: 'membership_suspended' | 'workspace_suspended' | 'workspace_deleting' | null;
  lastOpenedAt: number | null;
  favorite: boolean;
  /** Closing: read-only until it is deleted (M49). */
  closing: boolean;
  /** Precomputed lowercase name for search. */
  searchKey: string;
}

export function toWorkspace(raw: WorkspaceRaw): Workspace {
  const unavailableReason = raw.membership_status !== 'active' ? 'membership_suspended'
    : raw.workspace_status === 'suspended' ? 'workspace_suspended'
    : raw.workspace_status === 'deleting' ? 'workspace_deleting' : null;
  return {
    id: raw.workspace_id, name: raw.name, role: raw.role_name, available: unavailableReason === null, unavailableReason,
    closing: raw.workspace_status === 'closing',
    lastOpenedAt: raw.last_opened_at ? Date.parse(raw.last_opened_at) : null, favorite: raw.favorite,
    searchKey: raw.name.toLocaleLowerCase(),
  };
}

export async function listMyWorkspaces(transport: Transport, signal?: AbortSignal): Promise<Workspace[]> {
  const rows = await transport.rpc<WorkspaceRaw[]>('list_my_workspaces', {}, signal);
  return rows.map(toWorkspace);
}

/** Records recency only after the workspace actually opened; failure is non-fatal. */
export async function recordWorkspaceOpened(transport: Transport, workspaceId: string): Promise<void> {
  await transport.rpc('record_workspace_opened', { w: workspaceId });
}

/** Whether the workspace is closing (read-only until deleted) and whether the caller owns it. */
export interface WorkspaceClosure { status: 'active' | 'closing'; deleteAfter: number | null; closedBy: string | null; isOwner: boolean }
export async function readWorkspaceClosure(t: Transport, w: string, signal?: AbortSignal): Promise<WorkspaceClosure> {
  const rows = await t.rpc<{ status: string; delete_after: string | null; closed_by_name: string | null; is_owner: boolean }[]>('read_workspace_closure', { w }, signal);
  const r = rows[0];
  return { status: r?.status === 'closing' ? 'closing' : 'active', deleteAfter: r?.delete_after ? Date.parse(r.delete_after) : null, closedBy: r?.closed_by_name ?? null, isOwner: !!r?.is_owner };
}
/** The owner closes the workspace by typing its exact name; returns when it will be deleted. */
export const closeWorkspace = (t: Transport, w: string, request: string, confirmName: string) =>
  t.rpc<string>('close_workspace', { w, request, confirm_name: confirmName });
export const reopenWorkspace = (t: Transport, w: string) => t.rpc<string>('reopen_workspace', { w });

/** Workspace export (owner only): each section is one business table, fetched page by page. */
export const WORKSPACE_EXPORT_SECTIONS = ['companies', 'company_contacts', 'locations', 'orders', 'vehicles', 'order_stops', 'order_charges',
  'order_status_events', 'trucks', 'drivers', 'routes', 'route_stops', 'appointments', 'documents', 'document_versions', 'document_assignments',
  'invoices', 'invoice_lines', 'payments', 'payment_allocations', 'payment_reversals', 'storage_rates', 'storage_payments', 'roles', 'members'] as const;
export type WorkspaceExportSection = typeof WORKSPACE_EXPORT_SECTIONS[number];
/** Builds the export on this device: every section, every page, under one export ID. */
export async function exportWorkspace(t: Transport, w: string, exportId: string, onSection?: (done: number, total: number) => void, signal?: AbortSignal) {
  const sections: Record<string, unknown[]> = {};
  for (const [i, section] of WORKSPACE_EXPORT_SECTIONS.entries()) {
    const rows: unknown[] = [];
    for (let page = 0; ; page++) {
      const r = await t.rpc<{ rows: unknown[]; more: boolean }>('export_workspace_page', { w, export: exportId, section, page, lim: 1000 }, signal);
      rows.push(...r.rows);
      if (!r.more) break;
    }
    sections[section] = rows;
    onSection?.(i + 1, WORKSPACE_EXPORT_SECTIONS.length);
  }
  return { format: 'miraops.workspace-export.v1', workspace_id: w, export_id: exportId, sections };
}
export interface WorkspaceExport { id: string; requestedAt: number; requestedBy: string }
export async function listWorkspaceExports(t: Transport, w: string, signal?: AbortSignal): Promise<WorkspaceExport[]> {
  const rows = await t.rpc<{ id: string; requested_at: string; requested_by_name: string }[]>('list_workspace_exports', { w, lim: 20 }, signal);
  return rows.map(r => ({ id: r.id, requestedAt: Date.parse(r.requested_at), requestedBy: r.requested_by_name }));
}
/** The downloaded file: readable JSON with the time it was made, named by date. */
export const workspaceExportFile = (doc: object, now: Date) => ({
  name: `miraops-workspace-${now.toISOString().slice(0, 10)}.json`,
  text: JSON.stringify({ ...doc, exported_at: now.toISOString() }, null, 2) + '\n',
});

export type WorkspaceSort = 'recent' | 'name' | 'role';
export type WorkspaceFilter = 'all' | 'available' | 'unavailable' | 'pending';

/** The most recently opened workspace carries a "Recent" tag (per the chooser design). */
export const RECENT_MARKER_COUNT = 1;

export function arrangeWorkspaces(list: readonly Workspace[], search: string, filter: WorkspaceFilter, sort: WorkspaceSort, collator: Intl.Collator): Workspace[] {
  const needle = search.trim().toLocaleLowerCase();
  const filtered = list.filter(w =>
    (needle === '' || w.searchKey.includes(needle)) &&
    (filter === 'all' || (filter === 'available' ? w.available : filter === 'unavailable' ? !w.available : false)));
  const byName = (a: Workspace, b: Workspace) => collator.compare(a.name, b.name) || (a.id < b.id ? -1 : 1);
  return filtered.sort((a, b) => {
    if (sort === 'recent') return (b.lastOpenedAt ?? -1) - (a.lastOpenedAt ?? -1) || byName(a, b);
    if (sort === 'role') return collator.compare(a.role, b.role) || byName(a, b);
    return byName(a, b);
  });
}

export function recentIds(list: readonly Workspace[]): Set<string> {
  return new Set(list.filter(w => w.lastOpenedAt !== null)
    .sort((a, b) => (b.lastOpenedAt ?? 0) - (a.lastOpenedAt ?? 0))
    .slice(0, RECENT_MARKER_COUNT).map(w => w.id));
}

/** Current permission codes for showing controls. The server still checks every command. */
export async function readMyPermissions(transport: Transport, workspaceId: string, signal?: AbortSignal): Promise<Set<string>> {
  const rows = await transport.rpc<Array<{ permission: string }>>('read_my_permissions', { w: workspaceId }, signal);
  return new Set(rows.map(r => r.permission));
}
